import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useHistory, useLocation, useRouteMatch, Link } from "react-router-dom";

import { actions } from "venuescanner-js/constants/leads";
import { ViewContext } from "venuescanner-js/react/components/Inbox/context/ViewContext";
import { Spaces } from "venuescanner-js/services/modelService";
import observableToUseEffect from "venuescanner-js/react/components/Inbox/utils/observableToUseEffect";
import inboxService from "venuescanner-js/react/components/Inbox/services/inboxService";
import formatNumber from "venuescanner-js/helpers/formatNumber";
import { leadStatus } from "venuescanner-js/constants/thread";
import {
  AmenitiesLabels,
  FirstLevelAmenities,
  Layouts,
} from "venuescanner-js/constants/space";
import countries from "venuescanner-js/constants/countries.json";
import { Map } from "venuescanner-js/vanilla/components/map";
import Carousel from "venuescanner-js/react/components/Carousel";

import IconStar from "venuescanner-js/react/components/Objects/Icons/IconStar";
import IconCapacity from "venuescanner-js/react/components/Objects/Icons/IconCapacity";

// amenities icons
import IconAudioVisual from "venuescanner-js/react/components/Objects/Icons/IconAudioVisual";
import IconAirConditioning from "venuescanner-js/react/components/Objects/Icons/IconAirConditioning";
import IconCatering from "venuescanner-js/react/components/Objects/Icons/IconCatering";
import IconCloakroom from "venuescanner-js/react/components/Objects/Icons/IconCloakroom";
import IconDisabledAccess from "venuescanner-js/react/components/Objects/Icons/IconDisabledAccess";
import IconLectern from "venuescanner-js/react/components/Objects/Icons/IconLectern";
import IconOvernight from "venuescanner-js/react/components/Objects/Icons/IconOvernight";
import IconParking from "venuescanner-js/react/components/Objects/Icons/IconParking";
import IconPublicTransport from "venuescanner-js/react/components/Objects/Icons/IconPublicTransport";
import IconStage from "venuescanner-js/react/components/Objects/Icons/IconStage";
import IconWifi from "venuescanner-js/react/components/Objects/Icons/IconWifi";

// layout icons
import IconBoardroom from "venuescanner-js/react/components/Objects/Icons/IconBoardroom";
import IconCabaret from "venuescanner-js/react/components/Objects/Icons/IconCabaret";
import IconClassroom from "venuescanner-js/react/components/Objects/Icons/IconClassroom";
import IconDeskspace from "venuescanner-js/react/components/Objects/Icons/IconDeskspace";
import IconOpenSpace from "venuescanner-js/react/components/Objects/Icons/IconOpenSpace";
import IconPrintScanCopy from "venuescanner-js/react/components/Objects/Icons/IconPrintScanCopy";
import IconRestaurant from "venuescanner-js/react/components/Objects/Icons/IconRestaurant";
import IconTheatre from "venuescanner-js/react/components/Objects/Icons/IconTheatre";

import MatchAccept from "venuescanner-js/react/components/Inbox/components/Matches/MatchAccept";
import MatchIgnore from "venuescanner-js/react/components/Inbox/components/Matches/MatchIgnore";

const Match = ({ match }) => {
  const { isMobileDevice } = useContext(ViewContext);
  const [matchData, setMatchData] = useState(null);
  const [spaceData, setSpaceData] = useState(null);
  const [amenities, setAmenities] = useState(null);
  const [isForAdminOnly, setIsForAdminOnly] = useState(false);
  const [newMatchThreadId, setNewMatchThreadId] = useState(null);
  const [matchCurrency, setMatchCurrency] = useState(null);
  const [init, setInit] = useState(false);
  let history = useHistory();
  let { url } = useRouteMatch();
  const searchParamStatus = new URLSearchParams(useLocation().search).get(
    "status"
  );

  const [showDescription, setShowDescription] = useState(false);
  const [showLayouts, setShowLayouts] = useState(false);

  useEffect(() => {
    return observableToUseEffect({ match }, ({ match }) => setMatchData(match));
  }, [match]);

  useEffect(() => {
    if (spaceData) {
      // manage amenities data (the datastructure is mixed legacy hence the need to do this)
      const amenities = [];
      const amenitiesObject1 = Object.keys(spaceData.amenities);
      amenitiesObject1.forEach((amenity) => {
        if (amenity === FirstLevelAmenities.EXTRA) {
          spaceData.amenities.extra.checked &&
            spaceData.amenities.extra.opts.overnight_accomodation &&
            amenities.push("overnight_accomodation");
        } else {
          spaceData.amenities[amenity].checked && amenities.push(amenity);
        }
      });
      const amenitiesObject2 = Object.keys(spaceData.second_lvl_amenities);
      amenitiesObject2.forEach(
        (amenity) =>
          spaceData.second_lvl_amenities[amenity] && amenities.push(amenity)
      );

      const sortedAmenities = amenities.sort((a, b) => (a < b ? -1 : 1));
      const filteredAmenities = sortedAmenities.filter(
        (el) => !amenitiesBlackList.includes(el)
      );
      setAmenities(filteredAmenities);
      const map = new Map({ container: `#MatchSpace${spaceData.oid}Map` });
      map.marker({ center: true, ...spaceData });
      map.disableZoom();
    }
  }, [spaceData]);

  useEffect(() => {
    if (!init) {
      if (matchData?._id) {
        Spaces.sdp(matchData?.space?._id)
          .then((res) => {
            setSpaceData(res);
          })
          .catch((err) => {
            console.log("error - Match fetch space from spaces service");
          });

        setIsForAdminOnly(matchData.lead.status !== leadStatus.INTERESTED);
        setMatchCurrency(countries[matchData?._venue?.country_code || "GB"]);
        setInit(true);
      }
    }
  }, [matchData]);

  const viewNewMatchEnquiry = async () => {
    await inboxService.loadThread(newMatchThreadId);
    history.push(`/inbox/${newMatchThreadId}`);
  };

  const getIcon = (name) => {
    switch (name) {
      case "audiovisual":
        return <IconAudioVisual />;
      case "catering":
        return <IconCatering />;
      case "cloakroom":
        return <IconCloakroom />;
      case "disabled_access":
        return <IconDisabledAccess />;
      case "hvac":
        return <IconAirConditioning />;
      case "layout_boardroom":
        return <IconBoardroom />;
      case "layout_cabaret":
        return <IconCabaret />;
      case "layout_classroom":
        return <IconClassroom />;
      case "layout_deskspace":
        return <IconDeskspace />;
      case "layout_open_space":
        return <IconOpenSpace />;
      case "layout_restautrant":
        return <IconRestaurant />;
      case "layout_theatre":
        return <IconTheatre />;
      case "lectern":
        return <IconLectern />;
      case "overnight_accomodation":
        return <IconOvernight />;
      case "parking":
        return <IconParking />;
      case "print":
        return <IconPrintScanCopy />;
      case "public_transport":
        return <IconPublicTransport />;
      case "stage":
        return <IconStage />;
      case "wifi":
        return <IconWifi />;
      default:
        return null;
    }
  };

  const amenitiesBlackList = [
    "alcohol",
    "breakout_rooms",
    "catering_arrangements",
    "catering_facilities",
    "extra",
    "flipchart",
    "heat",
    "mic_audio",
    "natural_light",
    "outdoors",
    "phone",
    "refreshments",
    "table",
    "tea",
    "wedding",
    "white_board",
  ];

  // these amenities are in the data but look like legacy as they have no icon in the codebase
  //   alcohol
  //   breakout_rooms
  //   catering_arrangements
  //   catering_facilities
  //   extra
  //   flipchart
  //   heat
  //   mic_audio
  //   natural_light
  //   outdoors
  //   phone
  //   refreshments
  //   table
  //   tea
  //   wedding
  //   white_board

  return !spaceData || !matchData || !matchCurrency ? null : (
    <>
      <div className={isForAdminOnly ? "match isForAdminOnly" : "match"}>
        <div className="match__image">
          <Carousel
            id={spaceData.oid}
            altPrefix={spaceData.name}
            images={spaceData.photos}
            isMobile={isMobileDevice}
          />
        </div>

        <div className="match__body">
          <div className="match__copy">
            <div>
              {parseInt(spaceData.rating) > 0 && (
                <div className="match__copy__tags__rating">
                  <IconStar />
                  <h4>{spaceData.rating * 2}/10</h4>
                </div>
              )}
              {parseInt(matchData.lead.price || 0) > 0 && (
                <h4>
                  Quote:
                  <span>
                    {" "}
                    {" " +
                      matchCurrency.currency.symbol +
                      formatNumber(matchData.lead.price)}
                  </span>
                </h4>
              )}
            </div>
            <h3 className="match__copy__name">{spaceData.venue_name}</h3>
            <p
              className={
                showDescription
                  ? "match__copy__description"
                  : "match__copy__description match__copy__description--truncated"
              }
            >
              {spaceData.description}
              <br />
              {spaceData.venue.description}
            </p>
            <p
              className="match__copy__description__cta"
              onClick={() => setShowDescription(!showDescription)}
            >
              {showDescription ? "Read less" : "Read more"}
            </p>
          </div>
          {isMobileDevice && (
            <MatchCTA
              matchData={matchData}
              onViewEnquiry={viewNewMatchEnquiry}
            />
          )}
          <div
            className={
              !showLayouts ? "match__layouts" : "match__layouts isOpen"
            }
          >
            <div className="match__layouts__header">
              <div className="match__layouts__title">
                <IconCapacity />
                <p>
                  {spaceData.capacity_min} - {spaceData.capacity_max} people
                </p>
              </div>
              <p
                className="match__layouts__cta"
                onClick={() => setShowLayouts(!showLayouts)}
              >
                {showLayouts ? "Hide layouts" : "View layouts"}
              </p>
            </div>
            <div className="match__layouts__items">
              {Layouts.map(
                (layout) =>
                  spaceData[layout.tag] && (
                    <div key={layout.tag} className="match__layouts__layout">
                      {getIcon(layout.tag)}
                      <h4 className="match__layouts__layout__count">
                        {spaceData[layout.tag]}
                      </h4>
                      <p className="match__layouts__layout__copy small">
                        {layout.copy}
                      </p>
                    </div>
                  )
              )}
            </div>
          </div>

          {amenities?.length && (
            <>
              <h4>Amenities</h4>
              <div className="match__amenities">
                {amenities.map((amenity) => {
                  return (
                    <div key={amenity} className="match__amenities__amenity">
                      {getIcon(amenity)}
                      <span> {AmenitiesLabels[amenity]} </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <div className="match__location">
            <h4>Location</h4>
            <p className="match__location__address small">
              {spaceData.address}
            </p>
            <div
              className="match__location__map"
              id={`MatchSpace${spaceData.oid}Map`}
            />
          </div>

          {!isMobileDevice && (
            <MatchCTA
              matchData={matchData}
              onViewEnquiry={viewNewMatchEnquiry}
            />
          )}
        </div>

        {url === `/inbox/matches/${matchData._id}` && (
          <>
            {searchParamStatus === actions.ACCEPT.search_param && (
              <MatchAccept
                match={matchData}
                newMatchThreadId={newMatchThreadId}
                setNewMatchThreadId={setNewMatchThreadId}
                viewNewMatchEnquiry={viewNewMatchEnquiry}
              />
            )}
            {searchParamStatus === actions.IGNORE.search_param && (
              <MatchIgnore match={matchData} />
            )}
          </>
        )}
      </div>
    </>
  );
};

Match.propTypes = {
  match: PropTypes.object,
};

const MatchCTA = ({ matchData, onViewEnquiry }) => {
  return (
    <div className="match__ctas">
      {matchData.lead.status === leadStatus.ACCEPTED && (
        <button
          className="match__ctas__button match__ctas__button--view-enquiry p"
          onClick={() => onViewEnquiry}
        >
          View Enquiry
        </button>
      )}
      {matchData.lead.status === leadStatus.INTERESTED && (
        <>
          <Link
            className="match__ctas__button match__ctas__button--accept p"
            to={`/inbox/matches/${matchData._id}?status=${actions.ACCEPT.search_param}`}
          >
            Contact venue
          </Link>
          <Link
            className="match__ctas__button match__ctas__button--decline p"
            to={`/inbox/matches/${matchData._id}?status=${actions.IGNORE.search_param}`}
          >
            Decline
          </Link>
        </>
      )}
    </div>
  );
};

MatchCTA.propTypes = {
  matchData: PropTypes.object.isRequired,
  onViewEnquiry: PropTypes.func.isRequired,
};

export default Match;
